import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import IconButton from '@/components/ui/custom/buttons/IconButton';
import useHackathon from '@/data/hook/hackathon/useHackathon';
import useAuth from '@/data/hook/useAuth';
import { deleteProject } from '@/data/services/projectServices';
import { projectsQuerykeys } from '@/data/services/querykeys';
import { Project } from '@/models/Project';
import alert from '@/utils/shared/alert';
import { getErrorMessage } from '@/utils/getErrorMessage';
import ProjectPermissions from '@/utils/permissions/ProjectPermission';
import { ROUTES } from '@/utils/routes';
import { PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { LEI_FELCA } from '@/lib/growthbook';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { MouseEventHandler } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

type HackathonVotingProps = {
  project: Project;
};

export default function OwnerActions({ project }: HackathonVotingProps) {
  const queryClient = useQueryClient();

  const { user } = useAuth();
  const isLeiFelca = useFeatureIsOn(LEI_FELCA);

  const { hackathon } = useHackathon();

  const permissions = new ProjectPermissions({
    model: project,
    user,
    hackathon,
    isLeiFelca,
  });

  const { t } = useTranslation('translation', {
    keyPrefix: 'projects.card.actions',
  });

  const { mutate: removeProject, isLoading: isDeleting } = useMutation(
    deleteProject,
    {
      onSuccess: () => queryClient.invalidateQueries(projectsQuerykeys.list()),
      onError: e => alert.error(getErrorMessage(e)),
    },
  );

  const navigate = useNavigate();

  const onEdit: MouseEventHandler<HTMLButtonElement> = e => {
    e.preventDefault();
    e.stopPropagation();
    navigate(ROUTES.PROJECTS.EDIT.INFO(project.id), {
      state: project,
    });
  };

  const onDelete: MouseEventHandler<HTMLButtonElement> = e => {
    e.preventDefault();
    e.stopPropagation();
    removeProject(project.id);
  };

  return (
    <div className="flex gap-2">
      <ConditionalRenderer condition={permissions.canEdit()}>
        <IconButton
          icon={<PencilIcon />}
          tooltip={t('edit')}
          className="w-5 text-primary"
          onClick={onEdit}
          testId="editProject"
        />
      </ConditionalRenderer>
      <ConditionalRenderer condition={permissions.canDelete()}>
        <IconButton
          icon={<TrashIcon />}
          onClick={onDelete}
          loading={isDeleting}
          className="w-5 text-error"
          tooltip={t('delete')}
          testId="deleteProject"
        />
      </ConditionalRenderer>
    </div>
  );
}
